/**
 * The sales report: what came in, and what became of it.
 *
 * One question, asked of one group of rows. The window picks the leads
 * that **arrived** in it, and every figure then says what has become of
 * them since — see domain/salesReport.js for why it is a cohort and not
 * a diary, and for the four outcomes that add up to what came.
 *
 * Three cuts of the same cohort, in the order they get asked:
 *
 *   the figures     how many came, and how far they got
 *   where from      per source, because that is what buys advertising
 *   who has them    per owner, because that is what runs a team
 *
 * Every figure opens on the rows behind it. A number nobody can open is
 * a number nobody can act on, and the first question anybody asks of a
 * surprising one is "which ones?".
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import api from '../lib/api.js';
import { download } from '../lib/format.js';
import Icon from '../lib/icons.jsx';
import { Card, CardBody, EmptyState, Spinner } from '../components/Primitives.jsx';
import LeadDetail from '../components/modals/LeadDetail.jsx';
import { TZ_LABEL, stamp } from '../domain/demos.js';
import {
  RANGES, rangeOf, OUTCOMES, summarise, ownersOf, sourcesOf, share, lineOf
} from '../domain/salesReport.js';

/*
 * The figures, in the order a sentence needs them.
 *
 * `cut` is what the drill-down opens on, so the tile and the sheet
 * behind it are always the same rows — a tile that recomputed its own
 * list would eventually open on a count it did not show.
 */
const TILES = [
  { k: 'came', l: 'Leads came', i: 'flow', c: 'g-blue', s: 'arrived in this period' },
  { k: 'deal', l: 'Moved to deals', i: 'check', c: 'g-green', s: 'reached Won and left the board' },
  { k: 'converted', l: 'Became clients', i: 'users', c: 'g-cyan', s: 'on the books now' },
  { k: 'none', l: 'No response', i: 'eyeoff', c: 'g-amber', s: 'tried, and nobody answered' },
  { k: 'dropped', l: 'Not interested', i: 'x', c: 'g-slate', s: 'they said no, with a reason' }
];

/** One figure, and the rows behind it. A button, because it opens something. */
function Tile({ t, n, of, onOpen }) {
  const pct = t.k === 'came' ? null : share(n, of);
  return (
    <button type="button" className={`srtile ${t.c}`} onClick={() => onOpen(t.k)} title={`Show the ${t.l.toLowerCase()}`}>
      <div className={`ic ${t.c}`}><Icon name={t.i} solid /></div>
      <div className="srn">{n}{pct === null ? null : <em>{pct}%</em>}</div>
      <div className="srl">{t.l}</div>
      <div className="srsub">{t.s}</div>
      <Icon name="chev" className="srgo" />
    </button>
  );
}

/**
 * A cut of the cohort, one row per source or per owner.
 *
 * A six-column table of counts was the obvious shape and the wrong one:
 * most cells are empty on most rows, so it read as a grid of dashes with
 * the numbers scattered across a metre of screen, and nothing about it
 * said which sources matter.
 *
 * So each row is a bar. Its length is that row's share of everything
 * that came, and it is divided into the four outcomes — how much of
 * this source became a deal, is being chased, went quiet, was closed —
 * with the counts named underneath it. The shape is readable before any
 * of the numbers are, which is the whole job of the card.
 *
 * Every part of it opens the rows behind it: the name, a segment, a
 * chip. Segments carry a 2px gap so two touching colours never read as
 * one, and each is also named in a chip, so nobody has to tell the
 * colours apart to read the row.
 */
/* An icon for the well-known ways in, so a card is recognisable before
   its name is read. Anything unexpected gets the generic one rather
   than no icon at all. */
const ICONS = {
  Ads: 'mega', Referral: 'users', Direct: 'target', 'Organic Search': 'search',
  Email: 'mail', Imported: 'file', 'Social Media': 'chat', 'AI Assistant': 'wand',
  Manual: 'edit', Calendly: 'cal', Chatbot: 'chat', WhatsApp: 'sms',
  /* An uploaded list, by the segment it carried — see sourceOf. */
  'Direct import': 'sheet'
};

/* Every "<SEGMENT> import" gets the spreadsheet icon without having to be
   listed: the segments are the business's to invent. */
const iconFor = key => ICONS[key] || (/ import$/.test(key) ? 'sheet' : 'flow');

/* A colour per card, cycled by rank. Decoration: which source a card is
   about is said by its name, and the four outcome hues inside carry the
   meaning, so these are none of those four. */
const ACCENTS = ['a-indigo', 'a-sky', 'a-teal', 'a-violet', 'a-rose', 'a-amber', 'a-lime', 'a-coral'];

/**
 * How much of one source's leads became customers, as a ring.
 *
 * Drawn from a circle's dash rather than an arc path, so there is no
 * geometry to get wrong at 0% or 100%.
 */
function Ring({ pct }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <span className="spring" aria-hidden="true">
      <svg viewBox="0 0 40 40">
        <circle className="sprbg" cx="20" cy="20" r={r} />
        <circle className="sprfg" cx="20" cy="20" r={r} strokeDasharray={`${(c * (pct || 0)) / 100} ${c}`} />
      </svg>
      <b>{pct}%</b>
    </span>
  );
}

/**
 * One source, or one owner, as a card.
 *
 * The count leads, a thin bar under it says what became of those leads
 * without anybody reading a number, the four outcomes sit two by two,
 * and the one figure the card exists for — how many are customers now —
 * gets a panel of its own at the foot.
 *
 * Everything on it opens the rows behind it.
 */
function SourceCard({ r, kind, onOpen, rank }) {
  const open = cut => onOpen(`${kind}:${r.key}${cut ? `:${cut}` : ''}`);
  const wonPct = share(r.converted, r.came) || 0;
  const seen = OUTCOMES.filter(([k]) => r[k] > 0);

  return (
    <li className={`spcard ${ACCENTS[(rank - 1) % ACCENTS.length]}`}>
      <header>
        <span className="spic"><Icon name={kind === 'owner' ? 'users' : iconFor(r.key)} solid /></span>
        <button type="button" className="spname" onClick={() => open('')}>{r.key}</button>
        <span className="sprank">#{rank}</span>
      </header>

      <button type="button" className="sphero" onClick={() => open('came')}>
        <b>{r.came.toLocaleString()}</b><span>leads came</span>
      </button>

      {/* What became of them, at a glance. The bar is the whole of what
          came, so the part left unfilled is the leads nobody has decided
          about yet — which on most sources is most of the bar, and is
          the honest shape of it. */}
      <div className="spbar">
        {seen.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`spseg ${k}`}
            style={{ flexGrow: r[k] }}
            onClick={() => open(k)}
            title={`${r[k]} ${label.toLowerCase()} — ${share(r[k], r.came)}%`}
            aria-label={`${r[k]} ${label}`}
          />
        ))}
        {r.open ? <i className="spseg rest" style={{ flexGrow: r.open }} aria-hidden="true" /> : null}
      </div>

      {/* All three, always, so every card is the same shape and a zero
          is seen rather than inferred from a gap. */}
      <ul className="spgrid">
        {OUTCOMES.map(([k, label]) => (
          <li key={k}>
            <button
              type="button"
              className={`spbox ${k}${r[k] ? '' : ' nil'}`}
              onClick={() => (r[k] ? open(k) : null)}
              disabled={!r[k]}
            >
              <span className="spblab"><i />{label}</span>
              <span className="spbnum">
                <b>{r[k] ? r[k].toLocaleString() : '—'}</b>
                {r[k] ? <em>{share(r[k], r.came)}%</em> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className={`spwon${r.converted ? '' : ' nil'}`}
        onClick={() => (r.converted ? open('converted') : null)}
        disabled={!r.converted}
      >
        <span>
          <small>Became clients</small>
          <b>{r.converted ? r.converted.toLocaleString() : '—'}</b>
        </span>
        <Ring pct={wonPct} />
      </button>
    </li>
  );
}

function Breakdown({ title, note, rows, head, onOpen }) {
  return (
    <Card>
      <CardBody>
        <header className="srhead">
          <div><h4>{title}</h4><p>{note}</p></div>
        </header>

        {!rows.length ? (
          <EmptyState icon="search" tone="g-slate" title="Nothing arrived in this window" />
        ) : (
          <ol className="spcards">
            {rows.map((r, i) => (
              <SourceCard
                key={r.key}
                r={r}
                rank={i + 1}
                kind={head === 'Source' ? 'source' : 'owner'}
                onOpen={onOpen}
              />
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

export default function SalesReports() {
  const { S, crm, rows, toast } = useApp();
  const [key, setKey] = useState('30d');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [owner, setOwner] = useState('');
  const [source, setSource] = useState('');
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState('');

  const period = useMemo(() => rangeOf(key, { from, to }), [key, from, to]);
  const r = useMemo(
    () => summarise(S, crm, rows, { from: period.from, to: period.to, owner, source }),
    [S, crm, rows, period, owner, source]
  );

  const owners = useMemo(() => ownersOf(crm), [crm]);
  const sources = useMemo(() => sourcesOf(crm), [crm]);
  const t = r.total;

  /* One payload, three files. The screen has already worked the numbers
     out; the server lays them out rather than counting again. */
  const payload = () => ({
    title: 'XYRA — sales report',
    period: { label: period.label, from: period.from, to: period.to, days: period.days },
    timezone: TZ_LABEL,
    generatedAt: stamp(Date.now()),
    filters: { owner: owner || 'Everyone', source: source || 'Every source' },
    headline: TILES.map(x => ({
      measure: x.l,
      value: t[x.k],
      share: x.k === 'came' ? null : share(t[x.k], t.came),
      note: x.s
    })),
    columns: [['came', 'Came'], ...OUTCOMES.map(o => [o[0], o[1]]), ['converted', 'Became clients']]
      .map(([k, label]) => ({ key: k, label })),
    /* Same three the screen shows. "Came" is the total; what it does not
       add up to is the leads still in hand, which is not a column here
       for the same reason it is not a card. */
    bySource: r.bySource.map(x => ({ key: x.key, came: x.came, deal: x.deal, none: x.none, dropped: x.dropped, converted: x.converted })),
    byOwner: r.byOwner.map(x => ({ key: x.key, came: x.came, deal: x.deal, none: x.none, dropped: x.dropped, converted: x.converted })),
    leads: r.cohort.map(d => lineOf(S, crm, rows, d))
  });

  const save = async kind => {
    setSaving(kind);
    try {
      const body = payload();
      const blob = kind === 'xlsx' ? await api.sales.reportBook(body) : await api.sales.reportPdf(body);
      download(blob, `sales-report-${period.from}.${kind}`);
      toast(`Exported ${period.label.toLowerCase()}`);
    } catch (e) {
      toast(e.message || 'That export could not be built');
    } finally {
      setSaving('');
    }
  };

  const cut = owner || source
    ? ` · ${[owner, source].filter(Boolean).join(' · ')}`
    : '';

  return (
    <div className="sr">
      {/* ------------------------------------------------- the window */}
      <div className="srbar">
        <div className="srranges" role="tablist" aria-label="Period">
          {RANGES.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={key === k}
              className={`srr${key === k ? ' on' : ''}`}
              onClick={() => setKey(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {key === 'custom' ? (
          <div className="srdates">
            <label><span>From</span><input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} /></label>
            <label><span>To</span><input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} /></label>
          </div>
        ) : null}

        <div className="srf">
          <label htmlFor="sr-owner">Assigned to</label>
          <select id="sr-owner" value={owner} onChange={e => setOwner(e.target.value)}>
            <option value="">Everyone</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div className="srf">
          <label htmlFor="sr-source">Source</label>
          <select id="sr-source" value={source} onChange={e => setSource(e.target.value)}>
            <option value="">Every source</option>
            {sources.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div className="sracts">
          <button type="button" className="srout xls" disabled={!!saving} onClick={() => save('xlsx')} title="A formatted workbook">
            <span className="sroi">{saving === 'xlsx' ? <Spinner /> : <Icon name="sheet" />}</span>
            <span className="srot"><b>Excel</b><small>Formatted workbook</small></span>
          </button>
          <button type="button" className="srout pdf" disabled={!!saving} onClick={() => save('pdf')} title="Laid out for paper">
            <span className="sroi">{saving === 'pdf' ? <Spinner /> : <Icon name="file" />}</span>
            <span className="srot"><b>PDF</b><small>Laid out for paper</small></span>
          </button>
        </div>
      </div>

      <p className="srsays">
        <b>{period.label}</b> — {period.from} to {period.to} (end not included){cut}.
        {' '}{t.came} lead{t.came === 1 ? '' : 's'} arrived; every figure below says what has become of
        {' '}them since. All times {TZ_LABEL}.
      </p>

      {/* ------------------------------------------------ the figures */}
      <div className="srtiles">
        {TILES.map(x => <Tile key={x.k} t={x} n={t[x.k]} of={t.came} onOpen={setOpen} />)}
      </div>

      <Breakdown
        title="Where they came from"
        note="Every lead that arrived, by the source on the row — and how far each source's leads got."
        rows={r.bySource}
        head="Source"
        onOpen={setOpen}
      />

      <Breakdown
        title="Who is carrying them"
        note="The same leads, by whose name is on the row. Unassigned is nobody's yet."
        rows={r.byOwner}
        head="Owner"
        onOpen={setOpen}
      />

      {open ? (
        <LeadDetail
          cut={open}
          report={r}
          period={period}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
